'use client';
/**
 * card_carousel1 — 카드 캐러셀 «버튼·위치 표시만» 하는 작은 클라이언트 부품(라이브러리 0).
 * 카드 HTML 은 전부 서버가 그린다(SSR) — 이 부품은 같은 섹션의 `#railId` 목록을 scrollTo 로 넘기고 「1–4 / n」 을 갱신할 뿐이다.
 * prefers-reduced-motion 이면 smooth 를 끈다. 끝에서 버튼 disabled. JS 가 없어도 목록은 가로 스크롤(scroll-snap)로 다 보인다.
 *
 * region_home1b(2026-10-18) — 홈 히어로·카드 줄과 공용. 추가 옵션은 전부 기본 꺼짐(상세 캐러셀 마크업·동작 종전 그대로):
 *   · `#railId` 요소에 data-cc-rail="<선택자>" 가 있으면 그 안에서 선택자로 찾은 요소가 레일(홈 지역 필터가 목록을 갈아 끼워도 따라간다).
 *   · dots — 위치 점(누르면 그 카드로). pos=false — 「1 / n」 글자 숨김. focusScroll — 키보드 포커스가 카드에 들어오면 그 카드가 보이게 레일만 가로로.
 *   · 모든 스크롤은 사용자 조작(버튼·점·포커스)에서만 — 로드·수화 때 스크롤 호출 0(scrolltop FirstLoadScrollTop 호환). 자동 넘김 없음.
 *   · 다 보이면(넘길 게 없으면) 루트에 data-cc-all — 화면은 CSS 가 visibility 로만 숨긴다(자리 유지 · CLS 0).
 *
 * #4e — 넘김 이벤트(admin v6 「캐러셀 넘김」 카드 · conversion_events cta_name='carousel_swipe' · category=출처).
 *   기존 trackSwipe(lib/cta-track) 재사용(새 라이브러리 0). 출처 = track prop(detail_carousel 기본 · home_hero · home_row).
 *   사용자 조작에서만: 화살표(go) · 점(goTo) · 스와이프 끝(터치·포인터·휠 제스처 뒤 스크롤이 멈춘 프레임에 첫 카드가 바뀌었으면).
 *   로드·수화·포커스 스크롤·리사이즈 때는 0. 같은 부품에서 SWIPE_TRACK_GAP_MS 안의 연속 넘김은 1건만(쓰로틀).
 *   스와이프 끝 판정은 rAF 로 scrollLeft 가 SETTLE_FRAMES 프레임 그대로일 때(타이머 0 — 위 «타이머 호출 0» 규칙 유지).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { trackSwipe } from '@/lib/cta-track';

export type CarouselTrackSource = 'detail_carousel' | 'home_hero' | 'home_row';
/** 같은 부품 연속 넘김 이벤트 최소 간격(ms) */
export const SWIPE_TRACK_GAP_MS = 1000;
/** 스와이프 끝 — scrollLeft 가 이 프레임 수만큼 그대로면 멈춘 것으로 본다(≈130ms) · 상한 MAX_SETTLE_FRAMES */
const SETTLE_FRAMES = 8;
const MAX_SETTLE_FRAMES = 240;

/** 쓰로틀 판정(순수) — 처음이거나 간격이 지났으면 true. */
export function swipeTrackDue(lastMs: number | null, nowMs: number, gapMs: number = SWIPE_TRACK_GAP_MS): boolean {
  return lastMs == null || nowMs - lastMs >= gapMs;
}

/** trackSwipe 인자(순수) — from/to 는 0부터의 첫 보이는 카드 번호. 바뀌지 않았으면 null(보내지 않음). */
export function swipeTrackArgs(source: CarouselTrackSource, railId: string, from: number, to: number, via: 'arrow' | 'dot' | 'swipe'): { source: string; from_tab: string; to_tab: string } | null {
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return null;
  return { source, from_tab: `${railId}.${from + 1}`, to_tab: `${to + 1}@${via}` };
}

interface Props {
  railId: string;
  total: number;
  /** 섹션 이름(버튼 aria-label 앞머리) */
  label: string;
  /** region_home1b — 위치 점(버튼). 기본 없음. */
  dots?: boolean;
  /** region_home1b — 「1 / n」 글자. 기본 true. */
  pos?: boolean;
  /** region_home1b — 키보드 포커스 시 그 카드로 레일 가로 스크롤. 기본 false. */
  focusScroll?: boolean;
  /** #4e — 넘김 이벤트 출처(trackSwipe source). 기본 detail_carousel. */
  track?: CarouselTrackSource;
}

interface Range { first: number; last: number; n: number }

/** `#railId` → 실제 가로 스크롤 목록(data-cc-rail 선택자가 있으면 그 안에서 찾는다). */
export function railOf(railId: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const host = document.getElementById(railId);
  if (!host) return null;
  const sel = host.getAttribute('data-cc-rail');
  return sel ? host.querySelector<HTMLElement>(sel) : host;
}

function visibleRange(rail: HTMLElement): Range {
  const items = Array.from(rail.children) as HTMLElement[];
  const box = rail.getBoundingClientRect();
  let first = -1;
  let last = -1;
  items.forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const visible = Math.min(r.right, box.right) - Math.max(r.left, box.left);
    if (r.width > 0 && visible >= r.width * 0.6) {
      if (first < 0) first = i;
      last = i;
    }
  });
  if (first < 0) { first = 0; last = 0; }
  return { first, last, n: items.length };
}

/** 레일을 i 번째 카드 시작(scroll-padding 반영)으로 — 위치는 화면 좌표 차이로 잰다(offsetParent 무관). 사용자 조작에서만 부른다. */
export function scrollRailTo(rail: HTMLElement, i: number): void {
  const items = Array.from(rail.children) as HTMLElement[];
  const target = items[Math.min(items.length - 1, Math.max(0, i))];
  if (!target) return;
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const pad = parseFloat(getComputedStyle(rail).scrollPaddingLeft || '0') || 0;
  const left = rail.scrollLeft + target.getBoundingClientRect().left - rail.getBoundingClientRect().left - pad;
  rail.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
}

export default function CarouselNav({ railId, total, label, dots = false, pos = true, focusScroll = false, track = 'detail_carousel' }: Props) {
  const [range, setRange] = useState<Range | null>(null);
  const lastFire = useRef<number | null>(null);
  /** 사용자 조작 1건 → 이벤트 1건(쓰로틀). 로드·수화 경로에서는 부르지 않는다. */
  const fire = useCallback((from: number, to: number, via: 'arrow' | 'dot' | 'swipe') => {
    const args = swipeTrackArgs(track, railId, from, to, via);
    if (!args) return;
    const now = Date.now();
    if (!swipeTrackDue(lastFire.current, now)) return;
    lastFire.current = now;
    try { trackSwipe(args); } catch { /* 계측 실패는 넘김을 막지 않는다 */ }
  }, [track, railId]);

  const sync = useCallback(() => {
    if (!document.getElementById(railId)) return;
    const rail = railOf(railId);
    setRange(rail ? visibleRange(rail) : { first: 0, last: 0, n: 0 });
  }, [railId]);

  useEffect(() => {
    const host = document.getElementById(railId);
    if (!host) return;
    sync();
    let t = 0;
    const onScroll = () => { cancelAnimationFrame(t); t = requestAnimationFrame(sync); };
    // capture — 레일이 host 자신이든 그 안의 목록이든(갈아 끼워져도) 스크롤을 받는다(scroll 은 버블링하지 않는다).
    host.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    const mo = host.hasAttribute('data-cc-rail') && typeof MutationObserver !== 'undefined' ? new MutationObserver(onScroll) : null;
    mo?.observe(host, { childList: true, subtree: true });
    const onFocus = (e: FocusEvent) => {
      const rail = railOf(railId);
      const el = e.target as Node | null;
      if (!rail || !el || el === rail || !rail.contains(el)) return;
      const items = Array.from(rail.children) as HTMLElement[];
      const i = items.findIndex((c) => c.contains(el));
      if (i < 0) return;
      const r = items[i].getBoundingClientRect();
      const b = rail.getBoundingClientRect();
      if (r.left >= b.left - 1 && r.right <= b.right + 1) return;
      scrollRailTo(rail, i);
    };
    if (focusScroll) host.addEventListener('focusin', onFocus);
    // #4e — 스와이프 끝 계측: 레일 위 터치·포인터·휠(제스처) 시작 때 첫 카드를 적고, 스크롤이 멈춘 프레임에 바뀌었으면 1건.
    //   화살표·점(.cc-nav 안)은 go/goTo 가 직접 보낸다(여기서 다시 세지 않음). 포커스 스크롤·로드는 제스처가 아니라 0.
    let gFrom: number | null = null;
    let settleRaf = 0;
    let lastLeft = NaN;
    let still = 0;
    let frames = 0;
    const settle = () => {
      const rail = railOf(railId);
      if (!rail || gFrom === null) return;
      const left = rail.scrollLeft;
      still = left === lastLeft ? still + 1 : 0;
      lastLeft = left;
      frames++;
      if (still < SETTLE_FRAMES && frames < MAX_SETTLE_FRAMES) { settleRaf = requestAnimationFrame(settle); return; }
      const from = gFrom;
      gFrom = null;
      fire(from, visibleRange(rail).first, 'swipe');
    };
    const onGestureStart = (e: Event) => {
      const el = e.target as Element | null;
      if (el && typeof el.closest === 'function' && el.closest('.cc-nav')) return;
      const rail = railOf(railId);
      if (!rail || (el && !rail.contains(el as Node))) return;
      if (gFrom === null) gFrom = visibleRange(rail).first;
      if (e.type === 'wheel') onGestureEnd();
    };
    const onGestureEnd = () => {
      if (gFrom === null) return;
      cancelAnimationFrame(settleRaf);
      lastLeft = NaN; still = 0; frames = 0;
      settleRaf = requestAnimationFrame(settle);
    };
    host.addEventListener('touchstart', onGestureStart, { passive: true });
    host.addEventListener('pointerdown', onGestureStart, { passive: true });
    host.addEventListener('wheel', onGestureStart, { passive: true });
    host.addEventListener('touchend', onGestureEnd, { passive: true });
    host.addEventListener('pointerup', onGestureEnd, { passive: true });
    host.addEventListener('touchcancel', onGestureEnd, { passive: true });
    return () => {
      cancelAnimationFrame(t);
      cancelAnimationFrame(settleRaf);
      host.removeEventListener('touchstart', onGestureStart);
      host.removeEventListener('pointerdown', onGestureStart);
      host.removeEventListener('wheel', onGestureStart);
      host.removeEventListener('touchend', onGestureEnd);
      host.removeEventListener('pointerup', onGestureEnd);
      host.removeEventListener('touchcancel', onGestureEnd);
      host.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      mo?.disconnect();
      if (focusScroll) host.removeEventListener('focusin', onFocus);
    };
  }, [railId, sync, focusScroll, fire]);

  const go = (dir: -1 | 1) => {
    const rail = railOf(railId);
    if (!rail) return;
    const r = visibleRange(rail);
    const count = Math.max(1, r.last - r.first + 1);
    const to = Math.min(r.n - 1, Math.max(0, dir > 0 ? r.first + count : r.first - count));
    scrollRailTo(rail, to);
    fire(r.first, to, 'arrow');
  };
  const goTo = (i: number) => {
    const rail = railOf(railId);
    if (!rail) return;
    const from = visibleRange(rail).first;
    scrollRailTo(rail, i);
    fire(from, Math.min(rail.children.length - 1, Math.max(0, i)), 'dot');
  };

  const n = range ? range.n : total;
  const atStart = range ? range.first <= 0 : true;
  const atEnd = range ? range.last >= n - 1 : total <= 1;
  const text = range
    ? (range.first === range.last ? `${range.first + 1} / ${n}` : `${range.first + 1}–${range.last + 1} / ${n}`)
    : null;

  return (
    <div className="cc-nav" {...(atStart && atEnd ? { 'data-cc-all': '' } : {})}>
      {pos ? (
        <span className="cc-pos" aria-live="polite">
          {text ?? (
            <>
              <span className="cc-pos-m">{`1 / ${total}`}</span>
              <span className="cc-pos-d">{total > 1 ? `1–${Math.min(4, total)} / ${total}` : `1 / ${total}`}</span>
            </>
          )}
        </span>
      ) : null}
      <button type="button" className="nb" aria-controls={railId} aria-label={`${label} 이전 카드`} disabled={atStart} onClick={() => go(-1)}>
        <span aria-hidden="true">‹</span>
      </button>
      <button type="button" className="nb" aria-controls={railId} aria-label={`${label} 다음 카드`} disabled={atEnd} onClick={() => go(1)}>
        <span aria-hidden="true">›</span>
      </button>
      {dots && n > 1 ? (
        <span className="cc-dots" role="group" aria-label={`${label} 위치`}>
          {Array.from({ length: n }, (_, i) => {
            const on = range ? i >= range.first && i <= range.last : i === 0;
            return (
              <button
                key={i} type="button" className="cc-dot" aria-controls={railId} aria-label={`${label} ${i + 1}번째 카드`}
                {...(on ? { 'aria-current': 'true' as const } : {})} onClick={() => goTo(i)}
              />
            );
          })}
        </span>
      ) : null}
    </div>
  );
}
